import React, { useState } from 'react';
import { X, Phone, Mail, Calendar } from 'lucide-react';
import { scrollToSection } from '../utils/navigation';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'formation' | 'atelier';
  title: string;
  price: string;
  duration: string;
  description: string;
}

const InfoModal: React.FC<InfoModalProps> = ({ 
  isOpen, 
  onClose, 
  type, 
  title, 
  price, 
  duration, 
  description 
}) => {
  if (!isOpen) return null;

  const isAtelier = type === 'atelier';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Prix et durée */}
          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-3xl font-bold text-red-600">{price}</div>
                <div className="text-gray-600">{duration}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">
                  {isAtelier ? 'Atelier particulier' : 'Formation professionnelle'}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Description</h4>
            <p className="text-gray-600 leading-relaxed">{description}</p>
          </div>

          {/* Contenu spécifique */}
          {isAtelier ? (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Ce que vous apprendrez</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Préparation de la pâte à pizza traditionnelle</li>
                <li>• Techniques d'étalage à la main</li>
                <li>• Choix et préparation des garnitures</li>
                <li>• Cuisson et présentation</li>
                {title.includes('Socca') && <li>• Préparation de la Socca niçoise</li>}
              </ul>
            </div>
          ) : (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Programme détaillé</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Théorie et pratique intensive</li>
                <li>• Techniques professionnelles</li>
                <li>• Certification reconnue</li>
                <li>• Suivi post-formation</li>
                <li>• Accès aux ressources pédagogiques</li>
              </ul>
            </div>
          )}

          {/* Informations pratiques */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Informations pratiques</h4>
            <div className="space-y-2 text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-red-600" />
                <span>Lieu : Vallauris (06220)</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-red-600" />
                <span>Réservation : 06 60 47 67 40</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-red-600" />
                <span>Email : ecoledelapizza@gmail.com</span>
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-4">
            <button 
              onClick={() => {
                onClose();
                setTimeout(() => scrollToSection(isAtelier ? 'contact' : 'inscription'), 100);
              }}
              className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition-colors font-semibold text-center"
            >
              {isAtelier ? 'Réserver maintenant' : 'S\'inscrire'}
            </button>
            <button 
              onClick={() => {
                onClose();
                setTimeout(() => scrollToSection('contact'), 100);
              }}
              className="flex-1 border border-red-600 text-red-600 py-3 px-6 rounded-lg hover:bg-red-50 transition-colors font-semibold text-center"
            >
              Plus d'infos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoModal;